import Loading from 'components/Loading';
import SocialProof from 'components/SocialProof';
import Button from 'components/Button';
import Footer from 'components/Footer';
import Layout from 'containers/Layout';
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import Meta from 'utils/Meta';
import { sizes, width, colors, media, helpers } from 'styles/variables';
import RichText from 'utils/RichText';
import Stats from 'components/Stats';
import Usp from 'components/Usp';
import Faq from 'components/Faq';
import Testimonials from 'components/Testimonials';
import { navigate } from 'gatsby';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/fr';
import VisibilitySensor from 'react-visibility-sensor';
import firebase from 'gatsby-plugin-firebase';
import axios from 'axios';
import { log, fblog, eclog, splog, slog } from 'utils/log';
import 'url-search-params-polyfill';
import GO from 'utils/GO';

const Block = styled.div`
	padding: ${sizes[4]} ${sizes[2]};
	width: 100%;
	max-width: ${width.max}px;
	margin: 0 auto;
	display: grid;
	${media.notsmall} {
		grid-template-columns: 1fr 2fr;
	}
	${media.small} {
		grid-template-columns: 100%;
		padding-bottom: ${sizes[2]};
	}
	grid-gap: ${sizes[3]};
	h1 {
		font-size: 2.25rem;
	}
	p {
		font-size: 0.9rem;
	}
	a {
		display: block;
	}
	.cta {
		margin-top: ${sizes[2]};
	}
	&.plain,
	&.testimonials {
		grid-template-columns: 1fr;
		grid-auto-flow: row;
		text-align: center;
		.offer,
		.cta {
			display: none;
		}
	}
	&.plain {
		${media.notsmall} {
			padding: ${sizes[5]};
			h1 {
				font-size: 3rem;
			}
			a > div {
				max-width: 300px;
				margin: 0 auto;
			}
		}
		${media.small} {
			padding: ${sizes[4]} ${sizes[2]};
		}
		.cta {
			display: block;
		}
	}
`;

const Sticker = styled.div`
	position: sticky;
	top: ${sizes[2]};
	${media.small} {
		text-align: center;
		p,
		a {
			display: none;
		}
	}
`;

const Offer = styled.ul`
	background: ${colors.background[1]};
	padding: ${sizes[2]};
	font-size: 0.8rem;
	border-radius: ${sizes[0]};
	margin: ${sizes[2]} 0;
	li {
		padding-left: 26px;
		line-height: 1.25rem;
		margin-bottom: 5px;
		background: url(/checkbox_2.svg) left 0 no-repeat;
	}
	${media.small} {
		display: none;
	}
`;
const Results = styled.div`
	.cta {
		display: block;
	}
	h1 {
		font-size: 3rem;
		${media.notlarge} {
			font-size: 2.25rem;
		}
	}
	h2 {
		font-size: 1.75rem;
		text-transform: none;
		${media.notlarge} {
			font-size: 1.4rem;
		}
	}
	.weightLost {
		color: ${colors.pink.base};
		white-space: nowrap;
	}
	.weightPeriod {
		color: ${colors.teal.base};
		white-space: nowrap;
	}
	.dateStart,
	.dateEnd {
		font-size: 0.8rem;
		text-transform: uppercase;
		font-weight: 600;
		position: absolute;
		bottom: 0;
		display: grid;
		align-content: center;
		height: 20%;
	}
	.dateStart {
		color: ${colors.pink.base};
		text-align: left;
		left: ${sizes[2]};
	}
	.dateEnd {
		color: ${colors.teal.base};
		text-align: right;
		right: ${sizes[2]};
	}
`;
const ResultsTable = styled.div`
	width: 100%;
	display: grid;
	${media.notlarge} {
		padding: ${sizes[4]} 0;
	}
	${media.large} {
		grid-template-columns: repeat(2, 1fr);
	}
	grid-gap: ${sizes[2]};
	.graph {
		h2 {
			text-transform: capitalize;
		}
		${media.large} {
			grid-column: span 2;
		}
	}
	.card {
		.footer {
			&,
			* {
				padding: 0 !important;
			}
		}
		background: ${colors.background[0]};
		border-radius: ${sizes[0]};
		${helpers.shadow};
		div {
			padding: ${sizes[2]};
			text-align: center;
			&:last-of-type {
				padding: ${sizes[4]} ${sizes[2]};
			}
		}
		h2 {
			color: ${colors.teal.base};
			font-size: 2.5rem;
		}
		h2,
		p {
			display: block;
		}
		b {
			font-weight: bold;
		}
		ul {
			display: grid;
			grid-auto-flow: column;
			strong,
			small {
				display: block;
				text-align: center;
				font-size: 12px;
			}
			li {
				position: relative;
				&:last-of-type {
					&:before {
						content: '';
						display: block;
						position: Absolute;
						top: -7px;
						bottom: -7px;
						left: 0;
						right: 0;
						background: ${colors.pink.base};
						border-radius: ${sizes[0]};
						z-index: 0;
					}
					* {
						color: ${colors.background[0]};
						z-index: 1;
						position: relative;
					}
				}
			}
		}
		&Keto {
			grid-column: span 2;
			${media.notlarge} {
				grid-column: span 1;
			}
			h2 {
				font-size: 1.25rem;
				font-weight: normal;
				display: block;
				width: 100%;
				text-align: center;
				padding: ${sizes[2]};
				color: ${colors.foreground[0]};
			}
			> p {
				font-size: 12px;
				color: ${colors.foreground[2]};
				display: block;
				width: 100%;
				text-align: center;
				${media.large} {
					padding: ${sizes[2]} 20%;
				}
				${media.notlarge} {
					padding: ${sizes[2]};
				}
			}
			&Blocks {
				display: grid;
				grid-template-columns: repeat(3, 1fr);
				padding: 0 !important;
			}
			&Block {
				padding: 0 !important;
				h3 {
					font-size: 0.9rem;
					font-weight: normal;
					display: block;
					width: 100%;
					text-align: center;
					color: ${colors.foreground[2]};
				}
				p {
					font-size: 0.9rem;
					color: ${colors.teal.base};
					span {
						color: ${colors.teal.base};
						font-size: 2.5rem;
						font-weight: bold;
					}
				}
			}
		}
	}
`;
const Graph = styled.div`
	width: 100%;
	background: rgba(0, 0, 0, 0.05);
	${media.large} {
		height: 300px;
	}
	${media.notlarge} {
		height: 220px;
	}
	border-radius: ${sizes[0]};
	margin-top: ${sizes[2]};
	position: relative;
	overflow: hidden;
	hr {
		position: absolute;
		left: 0;
		right: 0;
		height: 1px;
		width: 100%;
	}
	img {
		position: absolute;
		top: 20%;
		left: 50px;
		bottom: 20%;
		right: 50px;
		width: calc(100% - 100px);
		height: 60%;
		${media.notlarge} {
			left: 30px;
			right: 30px;
			width: calc(100% - 60px);
		}
	}
	.dotStart,
	.dotEnd {
		position: absolute;
		width: 20px;
		height: 20px;
		border-radius: 20px;
		border: 3px solid ${colors.background[0]};
	}
	.dotStart {
		top: 20%;
		left: 50px;
		transform: translate(-50%, -50%);
		background: ${colors.pink.base};
		${media.notlarge} {
			left: 30px;
		}
	}
	.dotEnd {
		bottom: 21%;
		right: 50px;
		transform: translate(50%, 50%);
		background: ${colors.teal.base};
		${media.notlarge} {
			right: 30px;
		}
	}
	.valueStart,
	.valueEnd {
		position: absolute;
		border-radius: 20px;
		background: ${colors.background[0]};
		font-size: 0.7rem;
		font-weight: 600;
		padding: 6px;
	}
	.valueStart {
		top: 20%;
		left: 50px;
		transform: translate(-50%, calc(-50% - 26px));
		color: ${colors.pink.base};
		border: 2px solid ${colors.pink.base};
		${media.notlarge} {
			left: 30px;
		}
	}
	.valueEnd {
		bottom: 21%;
		right: 50px;
		transform: translate(50%, calc(50% - 26px));
		color: ${colors.teal.base};
		border: 2px solid ${colors.teal.base};
		${media.notlarge} {
			right: 30px;
		}
	}
`;
const Publishers = styled.div`
	padding: ${sizes[3]} 0;
	background: rgba(0, 0, 0, 0.025);
	> div {
		display: block;
		> div {
			display: grid;
			${media.large} {
				grid-auto-flow: column;
				justify-content: space-between;
			}
			${media.notlarge} {
				grid-auto-flow: row;
			}
			grid-gap: ${sizes[2]};
			> div {
				filter: grayscale(1);
				opacity: 0.75;
				mix-blend-mode: multiply;
			}
			img {
				height: 52px;
				${media.notlarge} {
					margin: 0 auto;
					display: block;
				}
			}
		}
	}
	h2 {
		font-size: 1.5rem;
		margin-bottom: ${sizes[3]};
		display: block;
		width: 100%;
		text-align: center;
	}
`;
const Benefits = styled.div`
	padding: ${sizes[3]} 0;
	> div {
		display: block;
		> div {
			display: grid;
			${media.large} {
				grid-template-columns: repeat(4, 1fr);
			}
			${media.notlarge} {
				grid-template-columns: repeat(2, 1fr);
			}
			grid-gap: ${sizes[2]};
			text-align: center;
			img {
				width: 80px;
				display: inline-block;
				margin-bottom: 5px;
			}
			p {
				display: inline-block;
				width: 100%;
				padding: 0 0 ${sizes[1]};
			}
		}
	}
	h2 {
		font-size: 1.5rem;
		margin-bottom: ${sizes[3]};
		display: block;
		width: 100%;
		text-align: center;
	}
	a {
		${media.large} {
			grid-column: span 4;
		}
		${media.notlarge} {
			grid-column: span 2;
		}
		> * {
			max-width: 380px;
			margin: 0 auto;
		}
	}
`;
const Reviews = styled.div`
	padding: ${sizes[3]} 0;
	background: rgba(0, 0, 0, 0.025);
	> div {
		display: block;
		> div {
			display: grid;
		}
	}
	h2 {
		font-size: 1.5rem;
		margin-bottom: ${sizes[3]};
		display: block;
		width: 100%;
		text-align: center;
	}
	a {
		${media.large} {
			grid-column: span 2;
		}
		> * {
			max-width: 380px;
			margin: 0 auto;
		}
	}
`;
const Letter = styled.div`
	${media.large} {
		grid-column: span 2;
	}
	grid-gap: 0;
	padding: ${sizes[3]} 0;
	background: rgba(0, 0, 0, 0.025);
	> div {
		grid-gap: 0;
	}
	> div > div {
		${media.large} {
			grid-column: span 2;
		}
		p {
			margin-bottom: ${sizes[1]};
		}
		img {
			height: 55px;
			mix-blend-mode: multiply;
			margin-top: -10px;
			margin-left: -10px;
		}
	}
`;

export default props => {
	const lang = props.pageContext.lang || '';
	moment.locale(lang.length === 0 || lang === 'keto' ? 'en' : lang);
	const [meta] = useState(require(`data/_meta`));
	const [translations] = useState(require(`data/${lang}_translations`));
	const [config] = useState(require(`data/${lang}_config`));
	const [overview] = useState(require(`data/${lang}_overview`));
	const [offer] = useState(require(`data/${lang}_offer`));
	const [values, setValues] = useState(null);

	const cta = overview.blocks[0].button_title;

	let id = typeof document !== 'undefined' ? new URLSearchParams(document.location.search).get('id') : '';
	useEffect(() => {
		if (!id) {
			id = localStorage.getItem('id');
			slog(`ov err6, id=${id}`, '');
			navigate(`/overview?id=${id}`);
		}
		if (id) {
			try {
				firebase
					.auth()
					.signInWithEmailAndPassword(atob(id), '_reshape_no_password')
					.then(user => {
						firebase
							.firestore()
							.collection('users')
							.doc(user.user.uid)
							.get()
							.then(docRef => {
								let doc = docRef.data();
								if (typeof doc !== 'undefined') {
									setValues(doc);
								} else {
									slog(`ov err1, ${JSON.stringify(user)}`, '');
									navigate('/');
								}
							})
							.catch(err => {
								slog(`ov err2, ${JSON.stringify(err)}`, '');
								navigate('/');
							});
					})
					.catch(err => {
						slog(`ov err3, ${JSON.stringify(err)}`, '');
						navigate('/');
					});
			} catch (err) {
				slog(`ov err4, ${JSON.stringify(err)}`, '');
				navigate('/');
			}
		} else {
			slog(`ov err5, ${JSON.stringify(document.location)}`, '');
			navigate('/');
		}
	}, []);

	const dynamicComponent = {
		stats: values ? <Stats values={values} {...props} /> : <></>,
		usp: <Usp {...props} />,
		faq: <Faq {...props} />,
		testimonials: <Testimonials {...props} />,
		plain: <></>,
	};

	let weightCurrent = values && values.json.weight_current;
	let weightTarget = values && values.json.weight_desired;
	let weightLost = weightCurrent - weightTarget;
	let weightUnits = 'kg';
	let weightWeek = Math.round(values && values.json.weight_current * 0.02 * 10) / 10;
	if (values && !values.json.metric) {
		weightLost = Math.round(weightLost * 2.20462262185 * 10) / 10;
		weightWeek = Math.round(weightWeek * 2.20462262185 * 10) / 10;
		weightCurrent = Math.round(weightCurrent * 2.20462262185 * 10) / 10;
		weightTarget = Math.round(weightTarget * 2.20462262185 * 10) / 10;
		weightUnits = 'lbs';
	}
	// let weightPeriodWeek = weightLost / weightWeek;
	let weightPeriodWeek = -49.4983 * Math.log(weightTarget / weightCurrent);
	let weightPeriodDays = weightPeriodWeek * 7;
	let weightPeriodMonths = Math.floor(-1 * moment().diff(moment().add(weightPeriodWeek, 'weeks'), 'months', true));

	if (values) {
		let obj = {};
		fblog('track', 'ViewContent', {
			content_name: 'overview',
		});
		splog('VIEW_CONTENT');
		splog('SIGN_UP');
		eclog(false, 'detail');
		for (var key in values.json) {
			if (values.json[key] === true) {
				log('Overview', 'Profile', key);
				fblog('trackCustom', 'overview_' + key);
				if (key.split('_').length) {
					obj[key.split('_')[0]] = key.split('_')[1];
				} else {
					obj[key.split('_')[0]] = key.split('_')[1];
				}
			} else if (typeof values.json[key] === 'string' || typeof values.json[key] === 'number') {
				obj[key] = values.json[key];
			}
		}
		if (values.json.newsletter) {
			fblog('track', 'Lead');
			splog('CUSTOM_EVENT_1');
		}
		if (values.json.weight_current) {
			let weightSegment = Math.floor(values.json.weight_current / 10) * 10;
			if (weightSegment < 40) {
				weightSegment = '<40';
			} else if (weightSegment > 150) {
				weightSegment = '>150';
			} else {
				weightSegment = `${weightSegment}_${weightSegment + 10}`;
			}
			console.log(weightSegment);
			log('Input', 'current_weight', weightSegment);
			fblog('trackCustom', 'Input', { action: 'current_weight', label: weightSegment });
		}
		if (values.json.calc.bmi_value) {
			let bmiSegment = Math.floor(values.json.calc.bmi_value / 2) * 2;
			if (bmiSegment < 18) {
				bmiSegment = '<18';
			} else if (bmiSegment > 60) {
				bmiSegment = '>60';
			} else {
				bmiSegment = `${bmiSegment}_${bmiSegment + 2}`;
			}
			console.log(bmiSegment);
			log('Input', 'current_bmi', bmiSegment);
			fblog('trackCustom', 'Input', { action: 'current_bmi', label: bmiSegment });
		}
		let web_exps = [];
		Object.keys(window._exps || {}).forEach(function(key, index) {
			if (window._exps[key]) {
				web_exps.push(key);
			}
		});
		let mailerlite_exps = {
			ab1: Math.round(Math.random()),
			ab2: Math.round(Math.random()),
			ab3: Math.round(Math.random()),
			ab4: Math.round(Math.random()),
		};
		let mail_exps = [];
		Object.keys(mailerlite_exps).forEach(function(key, index) {
			if (mailerlite_exps[key]) {
				mail_exps.push(key);
			}
		});
		let calc = {
			bmi_value: values.json.calc.bmi_value,
			bmi_title: values.json.calc.bmi_title,
			goal: values.json.calc.goal,
			goalLost: values.json.calc.goalLost,
			steps: values.json.calc.steps,
			calories: values.json.calc.tdee,
			period:
				weightPeriodWeek < 4
					? `in ${Math.max(1, Math.floor(weightPeriodWeek))} ${
							Math.max(1, Math.floor(weightPeriodWeek)) === 1 ? 'week' : 'weeks'
					  }`
					: `by ${moment()
							.add(weightPeriodWeek, 'weeks')
							.format('MMMM YYYY')}`,
		};

		const sessionJson = JSON.parse(sessionStorage.getItem('sessions'));
		console.log(`${sessionJson.source}__${sessionJson.medium}__${sessionJson.campaign}__${sessionJson.content}`);
		axios
			.post('/.netlify/functions/email', {
				data: { ...values.json },
				calc: { ...calc },
				mailerlite_exps,
				id: id,
				page: lang.length === 0 ? 'en' : lang,
				session: `${sessionJson.source}__${sessionJson.medium}__${sessionJson.campaign}__${sessionJson.content}`,
			})
			.then(e => true);
		// axios
		// 	.post('/.netlify/functions/sheet', {
		// 		data: { ...obj, ...calc, web_exps: web_exps.join(','), mail_exps: mail_exps.join(',') },
		// 		sheetName:
		// 			process.env.NODE_ENV === 'production' &&
		// 			!(typeof window !== 'undefined' && window.location.origin.indexOf('netlify') > -1)
		// 				? 'overview'
		// 				: '_dev_overview',
		// 	})
		// 	.then(e => true);
	}

	return (
		<>
			<Meta {...props} />
			<Helmet>
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'http://schema.org',
						'@type': 'WebPage',
						url: meta.domain,
						name: meta.title,
						description: meta.description,
					})}
				</script>
			</Helmet>
			<Layout>
				<style>{`.testimonials{display:none;}.sticker p{display:block;font-size:0;height:0;margin-bottom:-15px;}`}</style>
				<VisibilitySensor
					partialVisibility={true}
					onChange={v => v && log('Overview', 'Engagement', 'view_results')}
				>
					<Results
						onClick={() => {
							log('Overview', 'Engagement', 'click_results');
						}}
					>
						<Block>
							<div>
								<Sticker>
									<h1>{overview.headline}</h1>
									<a
										className="cta"
										href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}
									>
										<Button type={1} hue={'pink'} title={cta} />
									</a>
								</Sticker>
							</div>
							<ResultsTable className="results">
								<div className="graph">
									<h2>
										{values && values.name
											? `${values.name}, ${overview.start_today}`
											: overview.start_today}
										, {overview.follow_and_lose}{' '}
										<span className="weightLost">
											{weightLost} {weightUnits}
										</span>{' '}
										{weightPeriodWeek < 4 ? (
											<>
												in{' '}
												<span className="weightPeriod">
													{Math.max(1, Math.floor(weightPeriodWeek))}{' '}
													{Math.max(1, Math.floor(weightPeriodWeek)) === 1
														? translations.week
														: translations.weeks}
												</span>
											</>
										) : (
											<>
												by{' '}
												<span className="weightPeriod">
													{moment()
														.add(weightPeriodWeek, 'weeks')
														.format('MMMM YYYY')}
												</span>
											</>
										)}
									</h2>
									<Graph>
										{[...new Array(4)].map((item, index) => (
											<hr key={index} style={{ top: `${(100 / (4 + 1)) * (index + 1)}%` }} />
										))}
										<div className="dateStart">{moment().format('MMMM YYYY')}</div>
										<div className="dateEnd">
											{moment()
												.add(weightPeriodWeek, 'weeks')
												.format('MMMM YYYY')}
										</div>
										<img src="/line.svg" />
										<div className="valueStart">
											{weightCurrent} {weightUnits}
										</div>
										<div className="dotStart" />
										<div className="valueEnd">
											{weightTarget} {weightUnits}
										</div>
										<div className="dotEnd" />
									</Graph>
								</div>
								{lang === 'keto' && values && (
									<div className="card cardKeto">
										<h2>Keto Nutrition Requirements</h2>
										<div className="cardKetoBlocks">
											<div className="cardKetoBlock">
												<h3>Carbs</h3>
												<p>
													<span>{Math.floor((values.json.calc.tdee * 0.1) / 4)}</span>g
												</p>
											</div>
											<div className="cardKetoBlock">
												<h3>Protein</h3>
												<p>
													<span>{Math.floor((values.json.calc.tdee * 0.2) / 4)}</span>g
												</p>
											</div>
											<div className="cardKetoBlock">
												<h3>Fat</h3>
												<p>
													<span>{Math.floor((values.json.calc.tdee * 0.7) / 9)}</span>g
												</p>
											</div>
										</div>
										<p>
											These are your macronutrients - the numbers you need to hit each day to
											ensure your body enters Ketosis
										</p>
									</div>
								)}
								<div className="card">
									<div>
										<ul>
											{[...new Array(7)].map((item, index) => (
												<li key={index}>
													<strong>
														{moment()
															.add(index, 'days')
															.format('ddd')}
													</strong>
													<small>
														{moment()
															.add(index, 'days')
															.format('D')}
													</small>
												</li>
											))}
										</ul>
									</div>
									<hr />
									<div>
										<h2>
											-{weightWeek} {weightUnits}
										</h2>
										<div className="footer">
											<RichText data={overview.after_week} />
										</div>
									</div>
								</div>
								<div className="card">
									<div>
										<ul>
											{[...new Array(Math.min(weightPeriodMonths, 4) + 1)].map((item, index) => (
												<li key={index}>
													<strong>
														{moment()
															.add(
																(weightPeriodDays / Math.min(weightPeriodMonths, 4)) *
																	index,
																'days',
															)
															.format('MMM')}
													</strong>
													<small>
														{moment()
															.add(
																(weightPeriodDays / Math.min(weightPeriodMonths, 4)) *
																	index,
																'days',
															)
															.format('YYYY')}
													</small>
												</li>
											))}
										</ul>
									</div>
									<hr />
									<div>
										<h2>
											{weightPeriodWeek < 4 ? (
												<>
													{Math.max(1, Math.floor(weightPeriodWeek))}{' '}
													{Math.max(1, Math.floor(weightPeriodWeek)) === 1
														? translations.week
														: translations.weeks}
												</>
											) : (
												<>
													{Math.max(weightPeriodMonths, 1)}{' '}
													{weightPeriodMonths > 1 ? translations.months : translations.month}
												</>
											)}
										</h2>
										<div className="footer">
											<RichText data={overview.reach_target} />
										</div>
									</div>
								</div>
							</ResultsTable>
						</Block>
					</Results>
				</VisibilitySensor>
				<VisibilitySensor
					partialVisibility={true}
					onChange={v => v && log('Overview', 'Engagement', 'view_publishers')}
				>
					<Publishers
						onClick={() => {
							log('Overview', 'Engagement', 'click_publishers');
						}}
					>
						<Block>
							<h2>{config.publishers_title}</h2>
							<div>
								{config.publishers.map((item, index) => (
									<div key={index}>
										<img src={item.logo.url} />
									</div>
								))}
							</div>
						</Block>
					</Publishers>
				</VisibilitySensor>
				<VisibilitySensor
					partialVisibility={true}
					onChange={v => v && log('Overview', 'Engagement', 'view_benefits')}
				>
					<Benefits
						onClick={() => {
							log('Overview', 'Engagement', 'click_benefits');
						}}
					>
						<Block>
							<h2>{config.benefits_title}</h2>
							<div>
								{config.benefits.map((item, index) => (
									<div key={index}>
										<img src={item.image.url} />
										<p>{item.title}</p>
									</div>
								))}
								<a className="cta" href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}>
									<Button type={1} hue={'teal'} title={cta} />
								</a>
							</div>
						</Block>
					</Benefits>
				</VisibilitySensor>
				<VisibilitySensor
					partialVisibility={true}
					onChange={v => v && log('Overview', 'Engagement', 'view_reviews')}
				>
					<Reviews
						onClick={() => {
							log('Overview', 'Engagement', 'click_reviews');
						}}
					>
						<Block>
							<h2>{config.reviews_title}</h2>
							<div>
								<SocialProof reviews={config.reviews} />
								<a className="cta" href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}>
									<Button type={1} hue={'pink'} title={cta} />
								</a>
							</div>
						</Block>
					</Reviews>
				</VisibilitySensor>
				{overview.blocks.map((item, index) => (
					<div key={index}>
						<VisibilitySensor
							partialVisibility={true}
							onChange={v => v && log('Overview', 'Engagement', 'view_' + item.type)}
						>
							<Block
								className={item.type}
								onClick={() => {
									log('Overview', 'Engagement', 'click_' + item.type);
								}}
							>
								<div className="sticker">
									<Sticker>
										<RichText data={item.text} />
										<Offer className="offer">
											{offer.map((item, index) => (
												<li key={index}>{item.text}</li>
											))}
										</Offer>
										<a
											className="cta"
											href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}
										>
											<Button type={1} hue={'pink'} title={item.button_title} />
										</a>
									</Sticker>
								</div>
								{item.type !== 'plain' && (
									<div>
										<div>{dynamicComponent[item.type]}</div>
										<a
											className="cta --small"
											href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}
										>
											<Button type={1} hue={'pink'} title={item.button_title} />
										</a>
									</div>
								)}
							</Block>
						</VisibilitySensor>
						{item.type === 'usp' && (
							<>
								<VisibilitySensor
									partialVisibility={true}
									onChange={v => v && log('Overview', 'Engagement', 'view_letter')}
								>
									<Letter
										onClick={() => {
											log('Overview', 'Engagement', 'click_letter');
										}}
									>
										<Block>
											<RichText data={config.letter} />
											<a
												className="cta"
												href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}
											>
												<Button type={1} hue={'pink'} title={cta} />
											</a>
										</Block>
									</Letter>
								</VisibilitySensor>
							</>
						)}
					</div>
				))}
				<VisibilitySensor
					partialVisibility={true}
					onChange={v => v && log('Overview', 'Engagement', 'view_sticky')}
				>
					<a
						className="stickyCta"
						href={`${lang.length === 0 ? `` : `/${lang}`}/choose-plan?id=${id}`}
						onClick={() => {
							log('Overview', 'Engagement', 'click_sticky');
						}}
					>
						<Button type={1} hue={'pink'} title={cta} />
					</a>
				</VisibilitySensor>
				<style>{`
					@media only screen and (max-width: 768px) {div a.cta{display:none !important;}}
					@media only screen and (max-width: 768px) {div a.stickyCta{display:block;box-shadow:0 0 3px 0 rgba(0,0,0,0.1);position:fixed;bottom:0;left:0;right:0;padding:10px;background:#fff;z-index:999;}.results{padding-top:20px;}}
					@media only screen and (min-width: 769px) {div a.stickyCta{display:none;}}
				`}</style>
				<Footer {...props} />
				<Loading {...props} />
			</Layout>
		</>
	);
};
